import { useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Button, Group, Paper, Text, Textarea } from '@mantine/core'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import { IconSparkles } from '@tabler/icons-react'
import { getErrorMessage } from '../shared/errorUtils'
import type { ReferralDraftInput } from './referralTypes'
import { isDraftUnavailable, useReferralDraft } from './useReferralDraft'

type Props = {
  draftInput: ReferralDraftInput | null
  draftKey: string | null
  draftedFor: string | null
  onDrafted: (key: string) => void
  userEdited: boolean
  onUserEdited: () => void
  autoDraft: boolean
  findings: string
  onFindingsChange: (value: string) => void
  letter: string
  onLetterChange: (value: string) => void
  summary: string
  onSummaryChange: (value: string) => void
  /** True while a draft request is in flight, so the form can hold Review/Submit (PD-1825). */
  onDraftingChange?: (drafting: boolean) => void
}

export const ReferralLetterFields = (props: Props) => {
  const {
    draftInput,
    draftKey,
    draftedFor,
    onDrafted,
    userEdited,
    onUserEdited,
    autoDraft,
    onDraftingChange,
  } = props
  const draft = useReferralDraft()
  const [unavailable, setUnavailable] = useState(false)
  // Read inside the async draft: typing during an automatic draft must win.
  const editedRef = useRef(userEdited)
  editedRef.current = userEdited
  // Counts the clinician's edits to the letter and summary, so a draft that returns after
  // they typed (automatic or requested) never replaces what they wrote meanwhile.
  const editsRef = useRef(0)
  // Read inside the async draft: an auto-draft started for an old key must not land once the
  // clinician has moved on to a different recipient/specialty.
  const currentDraftKeyRef = useRef(draftKey)
  currentDraftKeyRef.current = draftKey
  const failedKeyRef = useRef<string | null>(null)
  const inFlightRef = useRef(false)
  // Bumped to force the auto-draft effect to retry the current key after a stale result was
  // discarded — nothing else about props/state changes in that case, so the effect wouldn't
  // otherwise re-run.
  const [retryTick, setRetryTick] = useState(0)
  // The findings the current letter was drafted from; typing new ones marks it stale but
  // never redrafts on its own.
  const [draftedFindings, setDraftedFindings] = useState<string | null>(null)
  const findingsRef = useRef(props.findings)
  findingsRef.current = props.findings

  const run = useCallback(
    async (mode: 'auto' | 'manual') => {
      if (!draftInput || !draftKey || inFlightRef.current) return
      inFlightRef.current = true
      onDraftingChange?.(true)
      const findingsSent = findingsRef.current
      const editsAtStart = editsRef.current
      let out: Awaited<ReturnType<typeof draft.mutateAsync>> | undefined
      let err: unknown
      try {
        out = await draft.mutateAsync(draftInput)
      } catch (e) {
        err = e
      } finally {
        inFlightRef.current = false
        onDraftingChange?.(false)
      }

      // The clinician moved on to a different recipient/specialty while this request (auto
      // or manual) was in flight: whichever way it settled, it's superseded — never commit,
      // never toast, never mark the old key as failed. Bump retryTick so the effect drafts
      // the now-current key.
      if (draftKey !== currentDraftKeyRef.current) {
        setRetryTick((t) => t + 1)
        return
      }

      if (out) {
        if (mode === 'auto' && editedRef.current) return
        if (editsRef.current !== editsAtStart) {
          if (mode === 'manual') {
            notifications.show({
              title: 'AI draft not applied',
              message:
                'You edited the letter while it was being drafted, so your text was kept. Regenerate to replace it.',
              color: 'yellow',
            })
          }
          return
        }
        props.onLetterChange(out.letter)
        props.onSummaryChange(out.clinicalSummary)
        setDraftedFindings(findingsSent)
        onDrafted(draftKey)
      } else if (isDraftUnavailable(err)) {
        setUnavailable(true)
      } else {
        failedKeyRef.current = draftKey
        notifications.show({
          title: 'AI draft failed',
          message: getErrorMessage(err),
          color: 'red',
        })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- props callbacks are stable setters in both forms
    [draftInput, draftKey, onDrafted, onDraftingChange],
  )

  // Leaving the step mid-draft must not leave the form holding Review/Submit.
  useEffect(() => () => onDraftingChange?.(false), [onDraftingChange])

  useEffect(() => {
    if (!autoDraft || unavailable || !draftInput || !draftKey) return
    if (draftKey === draftedFor || userEdited || failedKeyRef.current === draftKey) return
    void run('auto')
    // eslint-disable-next-line react-hooks/exhaustive-deps -- draftInput is rebuilt each render; draftKey identifies it; retryTick forces a retry after a stale auto-draft was discarded
  }, [autoDraft, unavailable, draftKey, draftedFor, userEdited, retryTick])

  const regenerate = () => {
    if (!userEdited) {
      void run('manual')
      return
    }
    modals.openConfirmModal({
      title: 'Replace your edits?',
      children: (
        <Text size="sm">
          Regenerating replaces the letter and the clinical summary, including your changes.
        </Text>
      ),
      labels: { confirm: 'Replace', cancel: 'Keep my edits' },
      confirmProps: { color: 'red' },
      onConfirm: () => void run('manual'),
    })
  }

  const drafted = draftedFor !== null
  // With auto-drafting on, an unedited letter is redrafted for the new target, so only an
  // edited one can be stale; with it off, any letter drafted for another target is stale.
  const stale =
    drafted && draftKey !== null && draftKey !== draftedFor && (userEdited || !autoDraft)
  const findingsChanged =
    drafted && draftedFindings !== null && props.findings.trim() !== draftedFindings.trim()

  return (
    <Paper p="md" withBorder>
      <Text fw={600} size="lg" mb="sm" className="text-[#6B21A8]">
        REFERRAL LETTER
      </Text>
      <Textarea
        label="Findings from the referring visit (optional)"
        description="Documented symptoms, examination findings or concerns. Used for the AI draft."
        autosize
        minRows={2}
        value={props.findings}
        onChange={(e) => props.onFindingsChange(e.currentTarget.value)}
      />
      <Group justify="space-between" align="flex-end" mt="md" mb={4}>
        <Text size="sm" fw={500}>
          Referral letter (Indication / Reason for Referral)
        </Text>
        {!unavailable && (
          <Button
            size="xs"
            variant="light"
            leftSection={<IconSparkles size={14} />}
            loading={draft.isPending}
            disabled={!draftInput}
            onClick={regenerate}
          >
            {drafted ? 'Regenerate with AI' : 'Draft with AI'}
          </Button>
        )}
      </Group>
      <Textarea
        aria-label="Referral letter"
        autosize
        minRows={8}
        value={props.letter}
        onChange={(e) => {
          editsRef.current += 1
          onUserEdited()
          props.onLetterChange(e.currentTarget.value)
        }}
      />
      <Textarea
        mt="sm"
        label="Clinical summary"
        autosize
        minRows={8}
        value={props.summary}
        onChange={(e) => {
          editsRef.current += 1
          onUserEdited()
          props.onSummaryChange(e.currentTarget.value)
        }}
      />
      {stale && (
        <Alert mt="sm" color="yellow" variant="light">
          The recipient, specialty or referral reason changed after this letter was drafted. Check
          the letter, or regenerate.
        </Alert>
      )}
      {findingsChanged && !stale && (
        <Alert mt="sm" color="yellow" variant="light">
          The visit findings changed after this letter was drafted. Regenerate to include them, or
          update the letter.
        </Alert>
      )}
      {drafted && (
        <Alert mt="sm" color="blue" variant="light">
          AI draft from the chart and the findings above. Review and edit both before sending — keep
          only documented information.
        </Alert>
      )}
    </Paper>
  )
}
